import { useState } from "react";
import { Trash2, Pencil, Check, X } from "lucide-react";
import { getExpiryStatus, formatDate } from "@/lib/utils-date";
import { formatKg, formatDecimalForInput } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePerm } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Sezione "Lotti" del prodotto (usata sia nella card mobile sia nella riga
 *  espansa della tabella desktop).
 *  - Ordinamento: scadenza più vicina in cima, senza scadenza in fondo.
 *  - Badge per riga: "Scaduto" quando applicabile.
 *  - Matita: PATCH /products/{pid}/lots/{lot_id} — apre form inline con
 *    numero lotto, scadenza (svuotabile) e giacenza precompilati.
 *  - Cestino: DELETE /products/{pid}/lots/{lot_id} con conferma nominativa.
 */
export default function LotsList({ product, onChanged }) {
  const canWrite = usePerm("products_write");
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Dialog Guardia Lotti: attivo quando la PATCH torna 409.
  // Contiene tutto ciò che serve per rifare la chiamata con `confirm`.
  const [conflictDlg, setConflictDlg] = useState(null); // { lotId, patch, kind, candidate }
  const isWeight = product.sale_unit === "weight" || (product.stock_kg !== null && product.stock_kg !== undefined);

  // Filtro anonimo seed: batch vuoto + no scadenza + giacenza 0 (creato al POST /products).
  const isAnonymousSeed = (l) =>
    (l.batch || "") === "" &&
    !l.expiry_date &&
    Number(l.stock || 0) === 0 &&
    Number(l.stock_kg || 0) === 0;

  const lots = (product.lots || [])
    .filter((l) => !isAnonymousSeed(l))
    .slice()
    .sort((a, b) => {
      const ax = a.expiry_date || "\uffff";
      const bx = b.expiry_date || "\uffff";
      return ax.localeCompare(bx);
    });

  if (lots.length === 0) return null;

  const fmtQty = (l) => {
    if (isWeight) return formatKg(l.stock_kg || 0);
    return `${Number(l.stock || 0)} pz`;
  };

  const handleDelete = async (lot) => {
    const label = lot.batch ? `Lotto ${lot.batch}` : "Lotto senza numero";
    if (!window.confirm(`Eliminare il ${label} con ${fmtQty(lot)}?`)) return;
    setBusyId(lot.id);
    try {
      await api.deleteLot(product.id, lot.id);
      toast.success("Lotto eliminato");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione lotto");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid={`lots-list-${product.id}`} className="pt-2 border-t border-[#EAE7E1] space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8E918C]">
        Lotti ({lots.length})
      </div>
      <div className="space-y-1">
        {lots.map((lot) => {
          if (editingId === lot.id) {
            return (
              <LotEditRow
                key={lot.id}
                lot={lot}
                isWeight={isWeight}
                busy={busyId === lot.id}
                onCancel={() => setEditingId(null)}
                onSave={async (patch) => {
                  setBusyId(lot.id);
                  try {
                    const res = await api.updateLot(product.id, lot.id, patch);
                    if (res?.merged_into) {
                      toast.success("Lotti uniti", {
                        description: "La modifica ha reso questo lotto identico a un altro: le giacenze sono state sommate.",
                      });
                    } else {
                      toast.success("Lotto aggiornato");
                    }
                    setEditingId(null);
                    onChanged && onChanged();
                  } catch (e) {
                    // Guardia Lotti: la PATCH ha rilevato un possibile duplicato
                    // e ha bloccato il salvataggio. Apriamo il dialog di
                    // conferma: il salvataggio resta aperto (form ancora in
                    // modifica) finché l'operatore non risponde.
                    const status = e?.response?.status;
                    const detail = e?.response?.data?.detail;
                    if (status === 409 && detail?.conflict) {
                      setConflictDlg({
                        lotId: lot.id,
                        patch,
                        kind: detail.conflict.kind,
                        candidate: detail.conflict.candidate_lot,
                        editedPreview: detail.conflict.edited_preview,
                      });
                    } else {
                      toast.error(typeof detail === "string" ? detail : "Errore aggiornamento lotto");
                    }
                  } finally {
                    setBusyId(null);
                  }
                }}
              />
            );
          }
          const st = getExpiryStatus(lot.expiry_date);
          const qty = isWeight ? Number(lot.stock_kg || 0) : Number(lot.stock || 0);
          const isEmpty = qty <= 0;
          const showBadge = !isEmpty && st.cls === "badge-expired";
          return (
            <div
              key={lot.id}
              data-testid={`lot-row-${lot.id}`}
              className="flex items-center gap-2 text-xs text-[#1A1C1A]"
            >
              <span className="font-mono flex-shrink-0 truncate max-w-[100px]" title={lot.batch || "Senza lotto"}>
                {lot.batch ? lot.batch : <span className="text-[#8E918C]">Senza lotto</span>}
              </span>
              <span className="text-[#5C5F5A] flex-shrink-0">
                {lot.expiry_date ? formatDate(lot.expiry_date) : <span className="text-[#8E918C]">—</span>}
              </span>
              <span className={`font-mono flex-shrink-0 ${isEmpty ? "text-[#8E918C]" : "text-[#1A1C1A]"}`}>
                {fmtQty(lot)}
              </span>
              {showBadge && (
                <span className={`${st.cls} text-[10px] font-semibold px-1.5 py-0.5 rounded-md`}>{st.label}</span>
              )}
              <div className="ml-auto flex items-center gap-0.5">
                {canWrite && (
                <button
                  type="button"
                  data-testid={`edit-lot-btn-${lot.id}`}
                  onClick={() => setEditingId(lot.id)}
                  disabled={busyId === lot.id}
                  className="text-[#8E918C] hover:text-[#2E4A3B] disabled:opacity-50 p-1"
                  title="Modifica questo lotto"
                >
                  <Pencil size={12} />
                </button>
                )}
                {canWrite && (
                <button
                  type="button"
                  data-testid={`delete-lot-btn-${lot.id}`}
                  onClick={() => handleDelete(lot)}
                  disabled={busyId === lot.id}
                  className="text-[#8E918C] hover:text-[#C65D47] disabled:opacity-50 p-1"
                  title="Elimina questo lotto"
                >
                  <Trash2 size={12} />
                </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ConflictDialog
        data={conflictDlg}
        onClose={() => setConflictDlg(null)}
        onDecide={async (decision) => {
          if (!conflictDlg) return;
          const { lotId, patch } = conflictDlg;
          setBusyId(lotId);
          try {
            const res = await api.updateLot(product.id, lotId, { ...patch, confirm: decision });
            if (res?.merged_into || decision === "merge") {
              toast.success("Lotti uniti", {
                description: "Le giacenze sono state sommate sul lotto esistente.",
              });
            } else {
              toast.success("Lotto aggiornato");
            }
            setConflictDlg(null);
            setEditingId(null);
            onChanged && onChanged();
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore aggiornamento lotto");
          } finally {
            setBusyId(null);
          }
        }}
      />
    </div>
  );
}

/** Dialog Guardia Lotti: si apre quando la PATCH torna 409 con `conflict`.
 *  Blocca il salvataggio finché l'operatore non sceglie una via, senza chiudere
 *  il form inline (così i valori dell'operatore restano lì se annulla).
 *  Da cellulare: pulsanti stacked, larghezza piena, altezza ≥ 44 px, testi che
 *  vanno a capo.
 */
function ConflictDialog({ data, onClose, onDecide }) {
  const open = !!data;
  const kind = data?.kind;
  const cand = data?.candidate || {};
  const preview = data?.editedPreview || {};
  const isSameBatch = kind === "same_batch_diff_expiry";
  const title = isSameBatch ? "Sono la stessa partita?" : "Numero simile a un lotto esistente";
  const description = isSameBatch ? (
    <>
      Il lotto <span className="font-mono font-semibold break-all">{cand.batch || "senza numero"}</span> esiste
      già con scadenza{" "}
      <span className="font-semibold">{cand.expiry_date || "—"}</span>.
      Stai per salvare la stessa sigla con scadenza{" "}
      <span className="font-semibold">{preview.expiry_date || "—"}</span>. Vuoi fondere in un solo lotto o
      mantenerli separati?
    </>
  ) : (
    <>
      Il numero <span className="font-mono font-semibold break-all">{preview.batch || "—"}</span> è simile
      al lotto già in magazzino <span className="font-mono font-semibold break-all">{cand.batch || "—"}</span>.
      Potrebbe essere un refuso. Vuoi fondere sul lotto esistente o tenere quello che hai scritto?
    </>
  );
  const mergeLabel = isSameBatch
    ? "Fondi con lotto esistente"
    : `Fondi con ${cand.batch || "lotto esistente"}`;
  const keepLabel = isSameBatch ? "Tieni separate" : "Tieni distinto";

  return (
    <AlertDialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <AlertDialogContent data-testid="lot-conflict-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="leading-snug [overflow-wrap:anywhere]">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <AlertDialogCancel
            data-testid="lot-conflict-keep-separate"
            onClick={() => onDecide("keep_separate")}
            className="w-full sm:w-auto min-h-[44px] whitespace-normal"
          >
            {keepLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="lot-conflict-merge"
            onClick={() => onDecide("merge")}
            className="w-full sm:w-auto min-h-[44px] whitespace-normal bg-[#2E4A3B] hover:bg-[#21382C]"
          >
            {mergeLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Form inline compatto per PATCH /lots/{id}. */
function LotEditRow({ lot, isWeight, busy, onCancel, onSave }) {
  const [batch, setBatch] = useState(lot.batch || "");
  const [expiry, setExpiry] = useState(lot.expiry_date || "");
  const [qtyStr, setQtyStr] = useState(
    isWeight
      ? formatDecimalForInput(lot.stock_kg || 0, 3)
      : String(Number(lot.stock || 0))
  );
  // Costo di acquisto del lotto. Chiave scelta in base a sale_unit del prodotto:
  // isWeight → cost_per_kg, altrimenti → cost_price. 3 decimali preservati
  // (evitiamo il toFixed(2) che degraderebbe 17,175 in 17,18 ad ogni salvataggio).
  const currentCost = isWeight ? lot.cost_per_kg : lot.cost_price;
  const [costStr, setCostStr] = useState(
    currentCost == null || currentCost === "" ? "" : formatDecimalForInput(currentCost, 3)
  );

  const submit = () => {
    const patch = {};
    if ((batch || "").trim() !== (lot.batch || "")) {
      patch.batch = (batch || "").trim();
    }
    // expiry: null se stringa vuota (svuota), altrimenti stringa YYYY-MM-DD
    const newExpiry = expiry ? expiry : null;
    if (newExpiry !== (lot.expiry_date || null)) {
      patch.expiry_date = newExpiry;
    }
    if (isWeight) {
      const raw = String(qtyStr).replace(",", ".").trim();
      const n = raw === "" ? null : Number(raw);
      if (n !== null && (Number.isNaN(n) || n < 0)) {
        return; // input non valido, non inviamo
      }
      const current = Number(lot.stock_kg || 0);
      if (n !== null && Math.abs(n - current) > 0.0005) {
        patch.stock_kg = n;
      }
    } else {
      const n = qtyStr === "" ? null : parseInt(qtyStr, 10);
      if (n !== null && (Number.isNaN(n) || n < 0)) return;
      if (n !== null && n !== Number(lot.stock || 0)) patch.stock = n;
    }
    // Costo: campo lasciato vuoto → costo invariato (non azzerato).
    // Se cambia rispetto al valore attuale del lotto, aggiungiamo la chiave
    // giusta (cost_per_kg per weight, cost_price per piece). Confronto con
    // tolleranza 0.0005 così i re-salvataggi senza modifica reale non
    // scattano un PATCH inutile — ma preserviamo 3 decimali (0.001 di soglia
    // sarebbe troppo stretta rispetto al round-trip float, 0.005 troppo
    // larga rispetto al terzo decimale). 0.0005 è a metà: 17,175 vs 17,174
    // scatta il patch, 17,175 vs 17,17500000001 no.
    const costRaw = String(costStr).replace(",", ".").trim();
    if (costRaw !== "") {
      const nCost = Number(costRaw);
      if (Number.isNaN(nCost) || nCost < 0) {
        return; // input non valido, non inviamo
      }
      const curCost = currentCost == null ? null : Number(currentCost);
      const changed = curCost == null || Math.abs(nCost - curCost) > 0.0005;
      if (changed) {
        if (isWeight) patch.cost_per_kg = nCost;
        else patch.cost_price = nCost;
      }
    }
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    onSave(patch);
  };

  return (
    <div
      data-testid={`lot-edit-row-${lot.id}`}
      className="flex flex-wrap items-center gap-1.5 text-xs bg-[#F9F8F6] border border-[#EAE7E1] rounded-md p-2"
    >
      <input
        type="text"
        value={batch}
        onChange={(e) => setBatch(e.target.value)}
        placeholder="Lotto"
        data-testid={`lot-edit-batch-${lot.id}`}
        className="font-mono w-24 h-7 px-2 rounded border border-[#EAE7E1] focus:outline-none focus:border-[#2E4A3B]"
      />
      <input
        type="date"
        value={expiry}
        onChange={(e) => setExpiry(e.target.value)}
        data-testid={`lot-edit-expiry-${lot.id}`}
        className="h-7 px-2 rounded border border-[#EAE7E1] focus:outline-none focus:border-[#2E4A3B]"
      />
      {expiry && (
        <button
          type="button"
          onClick={() => setExpiry("")}
          data-testid={`lot-edit-expiry-clear-${lot.id}`}
          className="text-[10px] text-[#8B6518] underline hover:text-[#6E5113]"
          title="Rimuovi scadenza"
        >
          togli
        </button>
      )}
      <input
        type="text"
        inputMode="decimal"
        value={qtyStr}
        onChange={(e) => setQtyStr(e.target.value)}
        placeholder={isWeight ? "kg" : "pz"}
        data-testid={`lot-edit-qty-${lot.id}`}
        className="font-mono w-20 h-7 px-2 rounded border border-[#EAE7E1] focus:outline-none focus:border-[#2E4A3B]"
      />
      <span className="text-[10px] text-[#8E918C]">{isWeight ? "kg" : "pz"}</span>
      <input
        type="text"
        inputMode="decimal"
        value={costStr}
        onChange={(e) => setCostStr(e.target.value)}
        placeholder={isWeight ? "€/kg" : "€/pz"}
        data-testid={`lot-edit-cost-${lot.id}`}
        title={isWeight ? "Costo €/kg" : "Costo €/pz"}
        className="font-mono w-20 h-7 px-2 rounded border border-[#EAE7E1] focus:outline-none focus:border-[#2E4A3B]"
      />
      <span className="text-[10px] text-[#8E918C]">{isWeight ? "€/kg" : "€/pz"}</span>
      <div className="ml-auto flex items-center gap-0.5">
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          data-testid={`lot-edit-save-${lot.id}`}
          className="text-[#2E4A3B] hover:bg-[#E8F3EC] disabled:opacity-50 p-1 rounded"
          title="Salva"
        >
          <Check size={14} />
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid={`lot-edit-cancel-${lot.id}`}
          className="text-[#8E918C] hover:bg-[#F4F1ED] disabled:opacity-50 p-1 rounded"
          title="Annulla"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
